import type { CSSProperties } from 'react';

interface ProgressProps {
  value: number;
  label?: string;
  color?: string;
  variant?: 'bar' | 'circle';
  className?: string;
  style?: CSSProperties;
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));

export function ProgressView({
  value,
  label,
  color = '#3b82f6',
  variant = 'bar',
  className,
  style,
}: ProgressProps) {
  const clamped = clamp(value);

  if (variant === 'circle') {
    const r = 36;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (clamped / 100) * circumference;
    return (
      <div
        className={`flex flex-col items-center gap-1${className ? ` ${className}` : ''}`}
        style={style}
      >
        <svg width="88" height="88" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle
            cx="44"
            cy="44"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 44 44)"
          />
          <text
            x="44"
            y="48"
            textAnchor="middle"
            className="text-sm font-medium fill-zinc-700 dark:fill-zinc-300"
          >
            {clamped}%
          </text>
        </svg>
        {label && <span className="text-xs text-zinc-500">{label}</span>}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1${className ? ` ${className}` : ''}`} style={style}>
      {label && <span className="text-xs text-zinc-500 dark:text-zinc-400">{label}</span>}
      <div className="h-2 w-full rounded-full bg-zinc-200 dark:bg-zinc-700">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${clamped}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
